import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice } from "../../lib/ui";
import "./purchasing.css";

/*
 * Create purchase order – port of purchase_order.php + process_po.php (API: POST /api/purchase-orders)
 * Pick a supplier and date, add items with the searchable product picker (fills in the purchase price),
 * set quantities and prices. Line totals and the PO total are worked out on the server too.
 */

function ProductPicker({ products, value, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  const listId = useId();
  const chosen = products.find((p) => p.id === value);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return products.filter((p) => !t || p.name.toLowerCase().includes(t) || (p.manufacturer || "").toLowerCase().includes(t)).slice(0, 50);
  }, [products, q]);
  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const pick = (p) => { onPick(p); setQ(""); setOpen(false); };
  const label = chosen ? `${chosen.name}${chosen.manufacturer ? ` (${chosen.manufacturer})` : ""}` : "";

  return (
    <div className="po-pick" ref={box}>
      <input className="cell" value={open ? q : label} placeholder="Search product or manufacturer"
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }} onChange={(e) => { setQ(e.target.value); setHi(0); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && open && list[hi]) { e.preventDefault(); pick(list[hi]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        role="combobox" aria-expanded={open} aria-controls={listId} aria-label="Product" />
      {open && (
        <ul className="po-pick-list" role="listbox" id={listId}>
          {list.length === 0 && <li className="none">No products match “{q}”</li>}
          {list.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === hi} className={i === hi ? "on" : ""} onMouseDown={(e) => { e.preventDefault(); pick(p); }}>
              <span className="nm">{p.name}</span>
              <span className="meta">{p.manufacturer || "No manufacturer"} · TZS {n2(p.purchase_price)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

let nextKey = 1;
const blankItem = () => ({ key: nextKey++, product_id: null, quantity: "", unit_price: "" });

export default function CreatePO() {
  const api = useApi();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState("");
  const [poDate, setPoDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/clients").then(setClients).catch((e) => setNotice({ tone: "err", text: e.message }));
    api.get("/api/products/options").then(setProducts).catch((e) => setNotice({ tone: "err", text: e.message }));
  }, [api]);

  const update = (key, patch) => setItems((list) => list.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const lineTotal = (it) => (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
  const total = items.reduce((s, it) => s + lineTotal(it), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) { setNotice({ tone: "err", text: "Add at least one item." }); return; }
    if (items.some((it) => !it.product_id)) { setNotice({ tone: "err", text: "Choose a product for every item." }); return; }
    setBusy(true); setNotice(null);
    try {
      const r = await api.post("/api/purchase-orders", {
        client_id: Number(clientId), po_date: poDate,
        items: items.map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })),
      });
      navigate(`/po/${r.po_id}`, { state: { message: r.message } });
    } catch (err) { setNotice({ tone: "err", text: err.message }); setBusy(false); }
  };

  return (
    <main className="pg" style={{ maxWidth: "64rem" }}>
      <PageHeader slug="purchase_order" actions={<Link to="/Approvedpo_list" className="btn"><i className="fas fa-list" /> Approved POs</Link>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <form onSubmit={submit}>
        <section className="panel" style={{ marginBottom: "1rem" }}>
          <div className="form" style={{ maxWidth: "none" }}>
            <div className="field"><label htmlFor="po-sup">Supplier<span className="req">*</span></label>
              <select id="po-sup" required value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Choose supplier</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="field"><label htmlFor="po-date">PO date<span className="req">*</span></label>
              <input id="po-date" type="date" required value={poDate} onChange={(e) => setPoDate(e.target.value)} /></div>
          </div>
        </section>

        <section className="panel" style={{ marginBottom: "1rem" }}>
          <div className="po-items-head">
            <h2>Order items <span className="pill-tag">{items.length}</span></h2>
            <button type="button" className="btn primary sm" onClick={() => setItems((l) => [...l, blankItem()])}><i className="fas fa-plus" /> Add item</button>
          </div>
          {items.length === 0 ? (
            <div className="empty"><i className="fas fa-box-open" />No items yet. Click “Add item” to start the order.</div>
          ) : (
            <div className="tbl-wrap" style={{ overflow: "visible" }}>
              <table className="tbl">
                <thead><tr><th>#</th><th style={{ width: "45%" }}>Product</th><th className="num">Quantity</th><th className="num">Unit price</th><th className="num">Total</th><th /></tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.key}>
                      <td>{i + 1}</td>
                      <td><ProductPicker products={products} value={it.product_id}
                        onPick={(p) => update(it.key, { product_id: p.id, unit_price: it.unit_price || String(p.purchase_price) })} /></td>
                      <td><input className="cell" type="number" min="1" required placeholder="0" value={it.quantity}
                        onChange={(e) => update(it.key, { quantity: e.target.value })} aria-label={`Quantity, item ${i + 1}`} /></td>
                      <td><input className="cell" type="number" min="0" step="0.01" required placeholder="0.00" value={it.unit_price}
                        onChange={(e) => update(it.key, { unit_price: e.target.value })} aria-label={`Unit price, item ${i + 1}`} /></td>
                      <td className="num mono">{n2(lineTotal(it))}</td>
                      <td><button type="button" className="btn sm" aria-label={`Remove item ${i + 1}`} onClick={() => setItems((l) => l.filter((x) => x.key !== it.key))}><i className="fas fa-trash-can" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="po-total">
          <div><span>Total order amount</span><small>{items.length} item{items.length === 1 ? "" : "s"}</small></div>
          <strong className="mono">TZS {n2(total)}</strong>
        </div>
        <div className="eod-actions" style={{ display: "flex", gap: ".6rem", justifyContent: "flex-end", marginTop: "1rem" }}>
          <button type="button" className="btn" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn primary" disabled={busy}><i className="fas fa-check" /> {busy ? "Creating…" : "Create purchase order"}</button>
        </div>
      </form>
    </main>
  );
}
